import { useState, useEffect } from 'react';
import type { CartItem } from '../page';
import { computeGst } from '@/lib/data';

interface CartPanelProps {
  cart: CartItem[];
  gstRate: number;
  gstInclusive?: boolean;
  onClose: () => void;
  onUpdateQuantity: (itemId: string, variantName: string | null, delta: number) => void;
  onRemoveItem: (itemId: string, variantName: string | null) => void;
  onPlaceOrder: () => void;
}

export default function CartPanel({ cart, gstRate, gstInclusive = false, onClose, onUpdateQuantity, onRemoveItem, onPlaceOrder }: CartPanelProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const subtotal = cart.reduce((sum, ci) => {
    const price = ci.selectedVariant ? ci.selectedVariant.price : ci.menuItem.price;
    const addonsTotal = ci.selectedAddons.reduce((a, ad) => a + ad.price, 0);
    return sum + (price + addonsTotal) * ci.quantity;
  }, 0);
  const { cgst, sgst, total } = computeGst(subtotal, gstRate, gstInclusive);

  const cartKey = (ci: CartItem) => `${ci.menuItem.id}-${ci.selectedVariant?.name || 'novar'}`;

  return (
    <div
      className={`fixed inset-0 z-40 flex transition-opacity duration-200 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />

      {/* Panel */}
      <div
        className={`relative ml-auto w-full max-w-md bg-background-50 h-full flex flex-col transition-transform duration-200 ${
          visible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <div className="flex items-center gap-2">
            <i className="ri-shopping-bag-2-fill text-xl text-accent-500"></i>
            <h2 className="text-lg font-semibold text-foreground-950 font-heading">
              Your Cart
              <span className="text-sm font-normal text-foreground-500 ml-1">
                ({cart.reduce((s, ci) => s + ci.quantity, 0)} items)
              </span>
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-foreground-600"></i>
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-foreground-400">
              <i className="ri-shopping-bag-2-line text-5xl mb-4"></i>
              <p className="text-sm font-medium">Your cart is empty</p>
              <p className="text-xs mt-1">Add items from the menu</p>
              <button onClick={handleClose} className="mt-4 text-sm text-primary-500 font-semibold cursor-pointer">
                Browse Menu
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {cart.map((ci) => {
                const price = ci.selectedVariant ? ci.selectedVariant.price : ci.menuItem.price;
                const addonsTotal = ci.selectedAddons.reduce((a, ad) => a + ad.price, 0);
                const lineTotal = (price + addonsTotal) * ci.quantity;
                const key = cartKey(ci);

                return (
                  <div key={key} className="flex gap-3 bg-background-100 rounded-xl p-3">
                    {/* Image */}
                    <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
                      {ci.menuItem.image ? (
                        <img src={ci.menuItem.image} alt={ci.menuItem.name} className="w-full h-full object-cover object-center" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-restaurant-2-line text-lg text-foreground-300"></i>
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0">
                          <h4 className="text-sm font-semibold text-foreground-950 truncate font-heading">{ci.menuItem.name}</h4>
                          {ci.selectedVariant && (
                            <p className="text-xs text-foreground-500">{ci.selectedVariant.name}</p>
                          )}
                        </div>
                        <button
                          onClick={() => onRemoveItem(ci.menuItem.id, ci.selectedVariant?.name || null)}
                          className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-100 text-foreground-400 hover:text-red-500 flex-shrink-0 ml-1 cursor-pointer transition-colors"
                        >
                          <i className="ri-delete-bin-6-line text-sm"></i>
                        </button>
                      </div>

                      {ci.selectedAddons.length > 0 && (
                        <p className="text-[10px] text-foreground-500 mt-0.5">
                          {ci.selectedAddons.map((a) => a.name).join(', ')}
                        </p>
                      )}
                      {ci.specialInstructions && (
                        <p className="text-[10px] text-foreground-400 italic mt-0.5 truncate">
                          "{ci.specialInstructions}"
                        </p>
                      )}

                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm font-bold text-foreground-950">₹{lineTotal}</span>
                        <div className="flex items-center gap-0 bg-background-200 rounded-lg">
                          <button
                            onClick={() => onUpdateQuantity(ci.menuItem.id, ci.selectedVariant?.name || null, -1)}
                            className="w-7 h-7 flex items-center justify-center text-foreground-600 cursor-pointer transition-colors"
                          >
                            <i className="ri-subtract-line text-xs"></i>
                          </button>
                          <span className="w-6 text-center text-xs font-bold text-foreground-950">{ci.quantity}</span>
                          <button
                            onClick={() => onUpdateQuantity(ci.menuItem.id, ci.selectedVariant?.name || null, 1)}
                            className="w-7 h-7 flex items-center justify-center text-primary-500 cursor-pointer transition-colors"
                          >
                            <i className="ri-add-line text-xs"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Bill summary + CTA */}
        {cart.length > 0 && (
          <div className="border-t border-background-200 px-5 py-4 bg-background-50">
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm text-foreground-600">
                <span>Subtotal</span>
                <span>₹{subtotal}</span>
              </div>
              <div className="flex justify-between text-sm text-foreground-600">
                <span>CGST ({gstRate / 2}%){gstInclusive ? ' · incl.' : ''}</span>
                <span>₹{cgst}</span>
              </div>
              <div className="flex justify-between text-sm text-foreground-600">
                <span>SGST ({gstRate / 2}%){gstInclusive ? ' · incl.' : ''}</span>
                <span>₹{sgst}</span>
              </div>
              <div className="flex justify-between text-base font-bold text-foreground-950 pt-2 border-t border-background-200">
                <span>Total</span>
                <span>₹{total}</span>
              </div>
            </div>
            <button
              onClick={onPlaceOrder}
              className="w-full py-3.5 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-base rounded-xl transition-all active:scale-[0.98] cursor-pointer whitespace-nowrap"
            >
              Place Order — ₹{total}
            </button>
          </div>
        )}

        {/* Empty cart bottom */}
        {cart.length === 0 && (
          <div className="border-t border-background-200 px-5 py-4">
            <button
              onClick={handleClose}
              className="w-full py-3 bg-background-200 hover:bg-background-300 text-foreground-600 font-semibold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              Browse Menu
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
